import { useEffect, useState } from "react";
import { Circle, CircleCheckBig, Plus, Trash2 } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useShopping } from "@/lib/hooks";
import { memberStyle } from "@/lib/meta";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export function ShoppingPage() {
  const { memberById } = useAuth();
  const { lists, items, addItem, toggleItem, deleteItem, clearChecked } = useShopping();
  const [activeList, setActiveList] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");

  useEffect(() => {
    if (!activeList && lists.length > 0) setActiveList(lists[0]!.id);
  }, [lists, activeList]);

  const listId = activeList ?? lists[0]?.id ?? null;
  const listItems = items.filter((i) => i.listId === listId);
  const todo = listItems.filter((i) => !i.checked);
  const done = listItems.filter((i) => i.checked);

  const submit = async () => {
    if (!name.trim() || !listId) return;
    const n = name.trim();
    const q = qty.trim() || null;
    setName("");
    setQty("");
    await addItem(listId, n, q);
  };

  return (
    <div className="space-y-4">
      {/* List switcher */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {lists.map((l) => {
          const count = items.filter((i) => i.listId === l.id && !i.checked).length;
          return (
            <button
              key={l.id}
              onClick={() => setActiveList(l.id)}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                listId === l.id ? "border-transparent bg-primary text-primary-foreground" : "bg-card hover:bg-accent",
              )}
            >
              {l.name}
              {count > 0 && (
                <span className={cn("rounded-full px-1.5 text-xs", listId === l.id ? "bg-primary-foreground/20" : "bg-muted")}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Add item */}
      <Card>
        <CardContent className="flex gap-2 p-3">
          <Input
            placeholder="Add an item…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <Input
            placeholder="Qty"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="w-20"
          />
          <Button size="icon" onClick={submit} aria-label="Add item">
            <Plus />
          </Button>
        </CardContent>
      </Card>

      {/* Items */}
      <div className="space-y-1">
        {todo.length === 0 && done.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">This list is empty. Add something above.</p>
        )}
        {todo.map((item) => {
          const m = item.addedBy ? memberById(item.addedBy) : null;
          return (
            <ItemRow key={item.id} name={item.name} qty={item.qty} checked={false} color={m?.color} onToggle={() => toggleItem(item.id, true)} onDelete={() => deleteItem(item.id)} />
          );
        })}

        {done.length > 0 && (
          <div className="flex items-center justify-between pt-4">
            <span className="text-sm font-medium text-muted-foreground">Done ({done.length})</span>
            <Button variant="ghost" size="sm" onClick={() => listId && clearChecked(listId)} className="text-muted-foreground">
              Clear
            </Button>
          </div>
        )}
        {done.map((item) => {
          const m = item.addedBy ? memberById(item.addedBy) : null;
          return (
            <ItemRow key={item.id} name={item.name} qty={item.qty} checked color={m?.color} onToggle={() => toggleItem(item.id, false)} onDelete={() => deleteItem(item.id)} />
          );
        })}
      </div>
    </div>
  );
}

function ItemRow({
  name,
  qty,
  checked,
  color,
  onToggle,
  onDelete,
}: {
  name: string;
  qty: string | null;
  checked: boolean;
  color?: string;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-accent/50">
      <button onClick={onToggle} aria-label={checked ? "Uncheck" : "Check"} className={checked ? "text-emerald-500" : "text-muted-foreground hover:text-foreground"}>
        {checked ? <CircleCheckBig className="size-5" /> : <Circle className="size-5" />}
      </button>
      <span className={cn("size-2 rounded-full", memberStyle(color).dot)} />
      <span className={cn("flex-1 truncate", checked && "text-muted-foreground line-through")}>{name}</span>
      {qty && <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{qty}</span>}
      <button onClick={onDelete} aria-label="Delete item" className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}
